import * as React from 'react';

import {safelyGetGraphQLErrorMessage} from 'nutshell-core/utils/graphql-errors';

import {colors} from 'shells/colors/colors';
import {FlashBanner} from 'shells/flash-banner/flash-banner';
// @ts-expect-error - imported from .js/.jsx file
import {SolidClockIcon} from 'shells/icon/svg/solid-clock-icon';
import {WarningIcon} from 'shells/icon/svg/warning-icon';
import {PageContentTwoColumn} from 'shells/layout/page-content-two-column';
import {StackLegacy} from 'shells/layout/stack/stack-legacy';
import {PathnameFieldWithButtons} from 'shells/pathname-field/pathname-field-with-buttons';
import {useManageSaveState} from 'shells/saved-setting-toast/use-manage-save-state';
import {SelectPickerGeneric} from 'shells/select-picker-generic/select-picker-generic';
import {TextFieldDebounced} from 'shells/textfield/textfield-debounced';
import {LabelWithContent} from 'shells/typography/label-with-content';

import type {
    MeetingFragment as Meeting,
    MeetingUpdateInput,
    GetSessionUser_loggedInUser as User,
} from 'nutshell-graphql-types';

import {GraphQLSelectActivityTypes} from '../core/select/graphql-select-activity-types';
import {useGetInstanceInfo} from '../core/use-get-instance-info';

import {AvailabilityConfig} from './availability/availability-config';
import {useUpdateMeeting} from './graphql/hooks';
import {
    getActivityIcon,
    getHostSectionTitleFromMeetingType,
    getMeetingUrlInfoText,
} from './helpers';
import {LocationSelect} from './location/location-select';
import {MeetingHostsSection, AddCohostsButton} from './meeting-hosts';
import {SchedulerFullFeatureGateModal} from './scheduler-full-feature-gate-modal';

import styles from './meeting-page.module.css';

type Props = {
    meeting: Meeting;
    meetingId: string;
    meetingPreview: React.ReactNode;
    refetchMeeting: () => any;
    refetchSchedulerConfig: () => void;
    user: User;
};

const DURATION_OPTIONS = [
    {label: '5 minutes', value: 5},
    {label: '10 minutes', value: 10},
    {label: '15 minutes', value: 15},
    {label: '20 minutes', value: 20},
    {label: '30 minutes', value: 30},
    {label: '45 minutes', value: 45},
    {label: '60 minutes', value: 60},
    {label: '1 hour 15 minutes', value: 75},
    {label: '1 hour 30 minutes', value: 90},
    {label: '2 hours', value: 120},
];

export function MeetingDetails(props: Props) {
    const [meetingDescription, setMeetingDescription] = React.useState<string | null | undefined>(
        props.meeting.description
    );
    const [confirmationMessage, setConfirmationMessage] = React.useState<string | null | undefined>(
        props.meeting.bookingConfirmationMessage
    );
    const [meetingName, setMeetingName] = React.useState<string | null | undefined>(
        props.meeting.name
    );
    React.useEffect(() => {
        setMeetingName(props.meeting.name);
    }, [props.meeting.name]);

    const [meetingDurationMinutes, setMeetingDurationMinutes] = React.useState<number>(
        props.meeting.durationMinutes
    );
    const [isSavingMeetingDetails, setIsSavingMeetingDetails] = React.useState<boolean>(false);
    const {toastState} = useManageSaveState(isSavingMeetingDetails, 1000);
    // Store which piece of data is saving so we can show a saving spinner by that label
    const [savingToastKey, setSavingToastKey] = React.useState<string | null | undefined>(null);
    const [newCustomSlug, setNewCustomSlug] = React.useState<string | null | undefined>(
        props.meeting.customSlug && props.meeting.creator.hasCustomizedMeetingUrl
            ? props.meeting.customSlug
            : props.meeting.urlSlug
    );
    const [customSlugError, setCustomSlugError] = React.useState<string | null | undefined>(null);
    const [isEditingCustomSlug, setIsEditingCustomSlug] = React.useState<boolean>(false);
    const [isFeatureGateModalOpen, setIsFeatureGateModalOpen] = React.useState<boolean>(false);

    const updateMeeting = useUpdateMeeting();

    const {sessionInstance} = useGetInstanceInfo();
    const hasFullAccess = sessionInstance
        ? !sessionInstance.isTrial && sessionInstance.schedulerAccessType === 'FULL_ACCESS'
        : false;
    const handleSaveCustomSlug = ({id, customSlug}: MeetingUpdateInput) => {
        setIsSavingMeetingDetails(true);
        setCustomSlugError(null);
        updateMeeting({
            id,
            customSlug,
        })
            .then((result) => {
                setIsEditingCustomSlug(false);
                setIsSavingMeetingDetails(false);
                if (
                    result.data &&
                    result.data.meetingUpdate &&
                    result.data.meetingUpdate.meeting &&
                    result.data.meetingUpdate.meeting.customSlug
                ) {
                    setNewCustomSlug(result.data.meetingUpdate.meeting.customSlug);
                }
            })
            .catch((e) => {
                setCustomSlugError(safelyGetGraphQLErrorMessage(e));
                setIsSavingMeetingDetails(false);
            });
    };

    const handleSave = ({
        id,
        name,
        description,
        bookingConfirmationMessage,
        durationMinutes,
        activityTypeId,
        locationType,
        location,
    }: MeetingUpdateInput) => {
        setIsSavingMeetingDetails(true);
        updateMeeting({
            id,
            name,
            description,
            durationMinutes,
            bookingConfirmationMessage,
            activityTypeId,
            locationType,
            location,
        }).then(() => {
            setIsSavingMeetingDetails(false);
            props.refetchSchedulerConfig();
            // Show the saving toast for 1 second
            setTimeout(() => {
                // setting to null so it doesn't show up for other fields too.
                setSavingToastKey(null);
            }, 1000);
        });
    };

    const [activityTypeId, setActivityTypeId] = React.useState<string>(
        props.meeting.activityType.id
    );

    const getAvailabilitySubLabel = () => {
        return (
            props.meeting.spansTimezones && (
                <div className={styles['label-warning']}>
                    {/* @ts-expect-error */}
                    <WarningIcon color={colors.orange} size={13} alignCenter={true} />
                    The hosts of this meeting are in different time zones.
                </div>
            )
        );
    };

    const isSoloMeeting = props.meeting.meetingType === 'SOLO';
    const canEditUrl =
        hasFullAccess &&
        isSoloMeeting &&
        (props.meeting.isManagedTemplate
            ? !props.meeting.assignments || props.meeting.assignments.totalCount === 0
            : props.meeting.creator.hasCustomizedMeetingUrl);

    return (
        <>
            {props.meeting.isManagedMeeting && (
                <FlashBanner type='info' leftAlignContent={true} className='pb-16' hasBorder={true}>
                    {props.meeting.isSevered
                        ? 'You have modified this managed meeting. It will not receive any manager edits.'
                        : 'This managed meeting was assigned to you. Any changes you make will prevent future manager edits.'}
                </FlashBanner>
            )}
            <PageContentTwoColumn
                withoutOuterPadding={true}
                leftContent={
                    <LabelWithContent
                        label='Preview'
                        spacing={8}
                        isFullHeight={true}
                        content={
                            <div className={styles['preview-container']}>
                                {props.meetingPreview}
                            </div>
                        }
                    />
                }
                rightContent={
                    <StackLegacy spacing={16}>
                        {!props.meeting.isManagedTemplate && (
                            <LabelWithContent
                                label={getHostSectionTitleFromMeetingType(props.meeting)}
                                labelButton={
                                    props.meeting.meetingType !== 'ROUND_ROBIN' &&
                                    !props.meeting.isManagedMeeting && (
                                        <AddCohostsButton meeting={props.meeting} />
                                    )
                                }
                                spacing={8}
                                content={
                                    <div className={styles['host-section-container']}>
                                        <MeetingHostsSection
                                            meeting={props.meeting}
                                            refetchMeeting={() => {
                                                props.refetchMeeting();
                                                props.refetchSchedulerConfig();
                                            }}
                                        />
                                    </div>
                                }
                            />
                        )}
                        <LabelWithContent
                            label='Name'
                            state={savingToastKey === 'name' ? toastState : 'initial'}
                            spacing={8}
                            content={
                                <TextFieldDebounced
                                    // @ts-expect-error
                                    name='meeting-name'
                                    placeholder='Discovery call, Sales Demo, etc.'
                                    value={meetingName || ''}
                                    delay={300}
                                    onChange={(newValue) => {
                                        setMeetingName(newValue);
                                    }}
                                    onBlur={() => {
                                        setSavingToastKey('name');
                                        if (meetingName) {
                                            handleSave({id: props.meetingId, name: meetingName});
                                        } else {
                                            setMeetingName(props.meeting.name);
                                        }
                                    }}
                                />
                            }
                        />
                        {isSoloMeeting && (
                            <LabelWithContent
                                label='Meeting URL'
                                spacing={8}
                                content={
                                    <div>
                                        <div className='flex gap-16 full-width'>
                                            <PathnameFieldWithButtons
                                                domainName={
                                                    !props.meeting.isManagedTemplate
                                                        ? props.meeting.creator
                                                              .schedulerRootMeetingsUrl
                                                        : null
                                                }
                                                value={newCustomSlug || ''}
                                                onChange={(newValue) => {
                                                    setNewCustomSlug(newValue);
                                                }}
                                                onSave={() => {
                                                    handleSaveCustomSlug({
                                                        id: props.meetingId,
                                                        customSlug: newCustomSlug,
                                                    });
                                                }}
                                                onCancel={() => {
                                                    setNewCustomSlug(
                                                        props.meeting.customSlug ||
                                                            props.meeting.urlSlug
                                                    );
                                                    setIsEditingCustomSlug(false);
                                                    setCustomSlugError(null);
                                                }}
                                                onSelectEdit={() => {
                                                    if (canEditUrl) {
                                                        setIsEditingCustomSlug(true);
                                                    }
                                                }}
                                                placeholder='your-meeting-name'
                                                isEditing={isEditingCustomSlug}
                                                hasError={Boolean(customSlugError)}
                                                isDisabled={!canEditUrl}
                                                showUpgradeButton={Boolean(
                                                    sessionInstance && !hasFullAccess
                                                )}
                                                onClickUpgradeButton={() => {
                                                    setIsFeatureGateModalOpen(true);
                                                }}
                                            />
                                        </div>
                                        {sessionInstance &&
                                            getMeetingUrlInfoText(
                                                props.meeting,
                                                customSlugError,
                                                hasFullAccess,
                                                props.user
                                            )}
                                    </div>
                                }
                            />
                        )}
                        <LabelWithContent
                            label='Duration'
                            spacing={8}
                            state={savingToastKey === 'duration' ? toastState : 'initial'}
                            content={
                                <SelectPickerGeneric
                                    controlIcon={SolidClockIcon}
                                    placeholder='Select duration'
                                    options={DURATION_OPTIONS}
                                    value={DURATION_OPTIONS.find((option) => {
                                        return option.value === meetingDurationMinutes;
                                    })}
                                    // @ts-expect-error
                                    onChange={(selected) => {
                                        setSavingToastKey('duration');
                                        setMeetingDurationMinutes(selected.value);
                                        handleSave({
                                            id: props.meetingId,
                                            durationMinutes: selected.value,
                                        });
                                    }}
                                />
                            }
                        />
                        <LabelWithContent
                            label='Location'
                            spacing={8}
                            content={
                                <LocationSelect
                                    locationType={props.meeting.locationType}
                                    onSave={handleSave}
                                    meeting={props.meeting}
                                />
                            }
                        />
                        <LabelWithContent
                            label='Activity type'
                            spacing={8}
                            state={savingToastKey === 'activityType' ? toastState : 'initial'}
                            content={
                                <GraphQLSelectActivityTypes
                                    // @ts-expect-error
                                    labelId='ActivityTypePicker'
                                    onChange={(selected) => {
                                        setSavingToastKey('activityType');
                                        setActivityTypeId(selected.id);
                                        handleSave({
                                            id: props.meetingId,
                                            activityTypeId: selected.id,
                                        });
                                    }}
                                    controlIcon={getActivityIcon(
                                        props.meeting.activityType.name || ''
                                    )}
                                    selectedId={activityTypeId}
                                    autoFocus={false}
                                    includeManageOption={true}
                                />
                            }
                        />
                        <LabelWithContent
                            label='Description'
                            state={savingToastKey === 'description' ? toastState : 'initial'}
                            spacing={8}
                            content={
                                <TextFieldDebounced
                                    // @ts-expect-error
                                    name='meeting-description'
                                    placeholder='Uncover prospect pain points and propose solutions, showcase product features and benefits to potential customers, etc.'
                                    value={meetingDescription || ''}
                                    multiline={true}
                                    delay={300}
                                    onChange={(newValue) => {
                                        setMeetingDescription(newValue);
                                    }}
                                    onBlur={() => {
                                        setSavingToastKey('description');
                                        handleSave({
                                            id: props.meetingId,
                                            description: meetingDescription,
                                        });
                                    }}
                                />
                            }
                        />
                        <LabelWithContent
                            label='Booking confirmation message'
                            state={
                                savingToastKey === 'bookingConfirmationMessage'
                                    ? toastState
                                    : 'initial'
                            }
                            spacing={8}
                            content={
                                <TextFieldDebounced
                                    // @ts-expect-error
                                    name='meeting-description'
                                    placeholder='Thanks for booking a meeting with me! I look forward to speaking with you.'
                                    value={confirmationMessage || ''}
                                    multiline={true}
                                    delay={300}
                                    onChange={(newValue) => {
                                        setConfirmationMessage(newValue);
                                    }}
                                    onBlur={() => {
                                        setSavingToastKey('bookingConfirmationMessage');
                                        handleSave({
                                            id: props.meetingId,
                                            bookingConfirmationMessage: confirmationMessage,
                                        });
                                    }}
                                />
                            }
                        />
                        <LabelWithContent
                            label='Availability'
                            sublabel={getAvailabilitySubLabel()}
                            spacing={8}
                            isFullHeight={true}
                            content={
                                <div className={styles['availability-container']}>
                                    <AvailabilityConfig
                                        availability={props.meeting.availability}
                                        postSave={props.refetchSchedulerConfig}
                                        hideTimezoneLabel={props.meeting.isManagedTemplate}
                                    />
                                </div>
                            }
                        />
                    </StackLegacy>
                }
            />
            <SchedulerFullFeatureGateModal
                isOpen={isFeatureGateModalOpen}
                onClose={() => setIsFeatureGateModalOpen(false)}
                isAdmin={props.user.permissions.canAccessSetup}
            />
        </>
    );
}
